/**
 * Aspect Admin — Analytics / performance / usage insights.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, toast } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { trendChart, donutChart, barRow, legend, sparkline } from "./charts.js";
import { TREND, DEVICE_SPLIT, ANALYTICS_TOP_SPACES } from "../mock_data.js";

const FUNNEL = [
  { label: "Tour link opened", value: 24381, pct: 100 },
  { label: "Entered walkthrough", value: 19992, pct: 82 },
  { label: "Visited 3+ rooms", value: 13898, pct: 57 },
  { label: "Completed tour", value: 9509, pct: 39 },
  { label: "Contacted agent", value: 2926, pct: 12 },
];

const STATS = [
  { label: "Avg session length", value: "3m 24s", delta: "+18s", spark: [8, 9, 9, 10, 11, 10, 12, 12, 13, 14, 13, 14] },
  { label: "Tour completion", value: "39%", delta: "+4.2pt", spark: [6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12] },
  { label: "Unique visitors", value: "16,204", delta: "+11%", spark: [5, 6, 8, 7, 9, 10, 9, 12, 13, 12, 14, 15] },
  { label: "AI cost / render", value: "€0.14", delta: "−9%", spark: [14, 13, 13, 12, 12, 11, 11, 10, 10, 10, 9, 9] },
];

export function renderAnalytics(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const maxSessions = Math.max(...ANALYTICS_TOP_SPACES.map((s) => s.sessions));
  const trend = { ...TREND, labels: TREND.labels.map((l) => t(l)), series: TREND.series.map((s) => ({ ...s, name: t(s.name) })) };

  const content = `
    ${pageHead(t("Analytics"), t("Engagement, performance, and AI usage across all published tours."), `
      <div class="ap-seg">
        <button type="button" class="ap-seg__btn" data-range="7d">${t("7d")}</button>
        <button type="button" class="ap-seg__btn ap-seg__btn--on" data-range="30d">${t("30d")}</button>
        <button type="button" class="ap-seg__btn" data-range="90d">${t("90d")}</button>
        <button type="button" class="ap-seg__btn" data-range="custom">${icon("calendar", 13)} ${t("Custom")}</button>
      </div>
      <button type="button" class="ap-btn" id="an-export">${icon("download", 15)} ${t("Export")}</button>
    `)}

    <div class="ap-grid ap-grid--kpi" style="margin-bottom:1.1rem;">
      ${STATS.map(
        (s) => `
        <div class="ap-panel ap-panel--hover ap-kpi">
          <div class="ap-kpi__top"><span class="ap-kpi__label">${t(s.label)}</span></div>
          <div class="ap-kpi__value" style="font-size:1.6rem;">${s.value}</div>
          <div class="ap-kpi__meta"><span class="ap-delta ap-delta--up">${s.delta}</span><span>${t("vs prior period")}</span></div>
          <div class="ap-kpi__spark">${sparkline(s.spark)}</div>
        </div>`
      ).join("")}
    </div>

    <div class="ap-grid ap-grid--main">
      <div class="ap-stack">
        <div class="ap-panel">
          <div class="ap-panel__head">
            <div>
              <h3 class="ap-panel__title">${t("Sessions over time")}</h3>
              <p class="ap-panel__sub">${t("All published tours · 30 days")}</p>
            </div>
            ${legend(trend.series)}
          </div>
          <div class="ap-panel__body">${trendChart(trend, { h: 260 })}</div>
        </div>

        <div class="ap-panel">
          <div class="ap-panel__head">
            <h3 class="ap-panel__title">${t("Top performing spaces")}</h3>
            <a class="ap-btn ap-btn--ghost ap-btn--sm" href="#/spaces">${t("All spaces")} ${icon("chevronRight", 13)}</a>
          </div>
          <div class="ap-tablewrap ap-scroll">
            <table class="ap-table">
              <thead><tr><th>${t("Space")}</th><th style="width:26%;">${t("Sessions")}</th><th class="ap-cell-num">${t("Avg time")}</th><th class="ap-cell-num">${t("Completion")}</th></tr></thead>
              <tbody>
                ${ANALYTICS_TOP_SPACES.map(
                  (s) => `
                  <tr>
                    <td><span class="ap-cell-main">${escapeHtml(s.name)}</span><span class="ap-cell-sub">${escapeHtml(s.client)}</span></td>
                    <td>
                      <div style="display:flex;align-items:center;gap:0.6rem;">
                        <div style="flex:1;">${barRow(Math.round((s.sessions / maxSessions) * 100))}</div>
                        <span style="font-variant-numeric:tabular-nums;min-width:3.2rem;text-align:right;font-weight:600;color:var(--ap-ink);">${s.sessions.toLocaleString()}</span>
                      </div>
                    </td>
                    <td class="ap-cell-num">${s.avg}</td>
                    <td class="ap-cell-num"><span class="ap-badge ${s.completion >= 60 ? "ap-badge--ok" : s.completion >= 45 ? "ap-badge--cyan" : "ap-badge--warn"}">${s.completion}%</span></td>
                  </tr>`
                ).join("")}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="ap-stack">
        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Visitor funnel")}</h3></div>
          <div class="ap-stack" style="gap:0.8rem;">
            ${FUNNEL.map(
              (f) => `
              <div class="ap-meter">
                <div class="ap-meter__head">
                  <span class="ap-meter__label" style="font-size:0.795rem;">${t(f.label)}</span>
                  <span class="ap-meter__val">${f.value.toLocaleString()} · ${f.pct}%</span>
                </div>
                <div class="ap-meter__track"><div class="ap-meter__fill" style="width:${f.pct}%;"></div></div>
              </div>`
            ).join("")}
          </div>
        </div>

        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Devices")}</h3></div>
          <div style="display:flex;align-items:center;gap:1rem;flex-wrap:wrap;">
            ${donutChart(DEVICE_SPLIT, { size: 150, centerLabel: "16.2k", centerSub: t("visitors") })}
            <div class="ap-stack" style="gap:0.45rem;flex:1;min-width:120px;">
              ${DEVICE_SPLIT.map(
                (d) => `
                <div style="display:flex;justify-content:space-between;font-size:0.78rem;color:var(--ap-slate);">
                  <span><span class="ap-legend__swatch" style="background:${d.color}"></span>${t(d.label)}</span>
                  <strong style="color:var(--ap-ink);">${d.value}%</strong>
                </div>`
              ).join("")}
            </div>
          </div>
        </div>

        <div class="ap-insight">
          <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag">${t("AI takeaway")}</span></div>
          <h4 class="ap-insight__title">${t("Mobile completion lags desktop by 21 points")}</h4>
          <p class="ap-insight__body">${t("Mobile visitors drop off most in rooms with heavy furniture counts. Consider enabling progressive loading for spaces over 120 m² — projected +8% mobile completion.")}</p>
          <div class="ap-insight__foot">
            <span class="ap-conf">${t("Confidence")} <span class="ap-conf__bars"><i class="on"></i><i class="on"></i><i class="on"></i></span></span>
            <button type="button" class="ap-btn ap-btn--soft ap-btn--sm" id="an-apply">${t("Enable for 3 spaces")}</button>
          </div>
        </div>
      </div>
    </div>
  `;

  const el = renderShell(root, "/analytics", content);
  if (!el) return;

  el.querySelector("#an-export")?.addEventListener("click", () => toast(root, t("Analytics export queued — you'll get an email"), "ok"));
  el.querySelector("#an-apply")?.addEventListener("click", () => toast(root, t("Progressive loading enabled for 3 large spaces"), "ok"));
  el.querySelectorAll("[data-range]").forEach((b) =>
    b.addEventListener("click", () => {
      el.querySelectorAll("[data-range]").forEach((x) => x.classList.remove("ap-seg__btn--on"));
      b.classList.add("ap-seg__btn--on");
      toast(root, t("Range set to {r}", { r: b.dataset.range }), "info");
    })
  );
}
